/**
 * AnalyticsFreshnessBar — Unified data freshness indicator for the analytics section.
 *
 * Shows "Sample Data" if any source uses seeded data, otherwise "Live Data".
 * Replaces per-chart badges for cleaner UX.
 *
 * Story 8.5 — Task 3
 */

type DataSourceInfo = {
	name: string;
	dataSource: string;
	dataAge: number;
};

type AnalyticsFreshnessBarProps = {
	sources: DataSourceInfo[];
};

/** Format data age (in seconds, matching API convention) to human-readable string */
function formatAge(seconds: number): string {
	if (seconds < 60) return "just now";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${String(minutes)}m ago`;
	const hours = Math.floor(minutes / 60);
	return `${String(hours)}h ago`;
}

export function AnalyticsFreshnessBar({ sources }: AnalyticsFreshnessBarProps) {
	if (sources.length === 0) return null;

	const hasSeeded = sources.some((s) => s.dataSource === "seeded");
	let worstAge = 0;
	for (const s of sources) {
		if (s.dataAge > worstAge) worstAge = s.dataAge;
	}

	if (hasSeeded) {
		return (
			<div
				className="flex items-center gap-2 rounded-lg bg-yellow-50 px-3 py-1.5"
				data-testid="freshness-bar"
			>
				<span className="inline-block h-2 w-2 rounded-full bg-yellow-400" />
				<span className="text-xs font-medium text-yellow-700">Sample Data</span>
			</div>
		);
	}

	return (
		<div
			className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-1.5"
			data-testid="freshness-bar"
		>
			<span className="inline-block h-2 w-2 rounded-full bg-green-400" />
			<span className="text-xs font-medium text-green-700">
				Live Data · Updated {formatAge(worstAge)}
			</span>
		</div>
	);
}
